import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Folder, Plus, Edit3, ToggleLeft, ToggleRight, Trash2, Upload, ArrowUpAZ, ArrowDownAZ, X, Search, Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import useModules from "../hooks/useModules";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import useBulkSelection from "../hooks/useBulkSelection";
import BulkActionsBar from "../components/BulkActionsBar";

// Basic permission check — kaafi admin/HR everywhere, dept mgr in their own module.
function canWriteProjectsHere(user, moduleId) {
    if (!user) return false;
    if (["kaafi_system_admin", "kaafi_hr_manager", "asl_hr_officer"].includes(user.role)) return true;
    if (user.role === "asl_department_manager" && user.module_scope === moduleId) return true;
    return false;
}

export default function Projects() {
    const { moduleCode } = useParams();
    const { modules } = useModules();
    const { user } = useAuth();
    const module = useMemo(
        () => (modules || []).find((m) => (m.code || "").toLowerCase() === (moduleCode || "").toLowerCase()),
        [modules, moduleCode],
    );

    // Iter60.p14 — filter + sort URL-sync so refresh preserves state.
    const [searchParams, setSearchParams] = useSearchParams();
    const urlSearch = searchParams.get("q") || "";
    const urlSort = searchParams.get("sort") || ""; // "" | "code_asc" | "code_desc"

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [editRow, setEditRow] = useState(null); // {id,name,code}
    const [confirmDelete, setConfirmDelete] = useState(null);
    // Iter60.p14 — Import from Excel dialog.
    const [showImport, setShowImport] = useState(false);
    // Local search debounced into the URL so we don't spam history entries.
    const [searchInput, setSearchInput] = useState(urlSearch);

    const canWrite = module && canWriteProjectsHere(user, module.id);

    // Iter56 — bulk delete for projects (delete-only per matrix).
    const bulk = useBulkSelection(rows, (r) => r.id);

    const load = useCallback(async () => {
        if (!module) return;
        setLoading(true);
        try {
            const params = {
                module_id: module.id,
                include_deleted: false,
            };
            if (urlSearch) params.search = urlSearch;
            if (urlSort) params.sort = urlSort;
            const { data } = await api.get("/projects", { params });
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [module, urlSearch, urlSort]);

    useEffect(() => { load(); }, [load]);

    // Iter60.p14 — debounce search input → URL param.
    useEffect(() => {
        const t = setTimeout(() => {
            const next = new URLSearchParams(searchParams);
            const v = searchInput.trim();
            if (v) next.set("q", v);
            else next.delete("q");
            if (next.toString() !== searchParams.toString()) {
                setSearchParams(next, { replace: true });
            }
        }, 250);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    // Iter60.p14 — sort toggle: none → asc → desc → none.
    const cycleSort = () => {
        const next = new URLSearchParams(searchParams);
        if (urlSort === "code_asc") next.set("sort", "code_desc");
        else if (urlSort === "code_desc") next.delete("sort");
        else next.set("sort", "code_asc");
        setSearchParams(next, { replace: true });
    };

    const activeCount = rows.filter((r) => r.active).length;
    const inactiveCount = rows.length - activeCount;

    return (
        <>
            <section className="space-y-5" data-testid="projects-page">
                <header className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Folder className="h-6 w-6 asl-navy-text" />
                        <div>
                            <h2 className="text-xl font-bold asl-navy-text">Projects</h2>
                            <p className="text-sm text-[color:var(--asl-muted)]">
                                Projects ({activeCount} active, {inactiveCount} inactive)
                            </p>
                        </div>
                    </div>
                    {canWrite && (
                        <div className="flex items-center gap-2">
                            {/* Iter60.p14 — Import from Excel */}
                            <Button
                                variant="outline"
                                onClick={() => setShowImport(true)}
                                data-testid="project-import-btn"
                                title="Bulk-import projects from an Excel or CSV file"
                            >
                                <Upload className="h-4 w-4 mr-1" /> Import from Excel
                            </Button>
                            <Button
                                onClick={() => setShowNew(true)}
                                className="asl-navy-bg text-white hover:opacity-90"
                                data-testid="project-new-btn"
                            >
                                <Plus className="h-4 w-4 mr-1" /> New Project
                            </Button>
                        </div>
                    )}
                </header>

                {/* Iter60.p14 — Filter + Sort row. */}
                <div className="asl-card p-3 flex flex-wrap items-center gap-3" data-testid="projects-filter-bar">
                    <div className="relative flex-1 min-w-[220px] max-w-[420px]">
                        <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--asl-muted)]" aria-hidden="true" />
                        <Input
                            type="text"
                            placeholder="Filter by Job # or Project Name…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="pl-8 pr-8 h-9"
                            data-testid="projects-filter-input"
                        />
                        {searchInput && (
                            <button
                                type="button"
                                onClick={() => setSearchInput("")}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                                aria-label="Clear filter"
                                data-testid="projects-filter-clear"
                            >
                                <X className="h-3 w-3 text-[color:var(--asl-muted)]" />
                            </button>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={cycleSort}
                        data-testid="projects-sort-toggle"
                        data-sort={urlSort || "none"}
                        title={
                            urlSort === "code_asc"
                                ? "Sorted by Job # ascending (numeric). Click for descending."
                                : urlSort === "code_desc"
                                ? "Sorted by Job # descending (numeric). Click to clear."
                                : "Sort by Job # (numeric)."
                        }
                    >
                        {urlSort === "code_asc" && <ArrowUpAZ className="h-3.5 w-3.5 mr-1" />}
                        {urlSort === "code_desc" && <ArrowDownAZ className="h-3.5 w-3.5 mr-1" />}
                        {!urlSort && <ArrowUpAZ className="h-3.5 w-3.5 mr-1 opacity-40" />}
                        {urlSort === "code_asc"
                            ? "Job # ↑"
                            : urlSort === "code_desc"
                            ? "Job # ↓"
                            : "Sort by Job #"}
                    </Button>
                    <span className="text-[11px] text-[color:var(--asl-muted)]" data-testid="projects-filter-count">
                        {loading ? "Loading…" : `${rows.length} project${rows.length === 1 ? "" : "s"}`}
                    </span>
                </div>

                <div className="asl-card p-0 overflow-hidden">
                    {canWrite && (
                        <div className="px-3 pt-3">
                            <BulkActionsBar
                                resource="projects"
                                selectedIds={bulk.selectedIds}
                                onClear={bulk.clear}
                                onRefresh={load}
                                entityLabel="project"
                                canApprove={false}
                            />
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="asl-table asl-table--zebra" data-testid="projects-table">
                            <thead>
                                <tr className="text-left text-xs text-[color:var(--asl-muted)] border-b border-[color:var(--asl-border,#E5E7EB)] bg-[#FAFAFB]">
                                    {canWrite && (
                                        <th className="px-3 py-2 w-8">
                                            <Checkbox
                                                checked={bulk.allVisibleSelected}
                                                onCheckedChange={bulk.toggleAll}
                                                data-testid="bulk-select-all"
                                            />
                                        </th>
                                    )}
                                    <th className="px-3 py-2">Job #</th>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2">Created</th>
                                    <th className="px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={canWrite ? 6 : 5} className="px-3 py-6 text-center text-[color:var(--asl-muted)]">Loading…</td></tr>
                                ) : rows.length === 0 ? (
                                    <tr><td colSpan={canWrite ? 6 : 5} className="px-3 py-6 text-center text-[color:var(--asl-muted)] italic">
                                        {urlSearch
                                            ? `No projects match "${urlSearch}".`
                                            : (canWrite
                                                ? "No projects yet. Click \"New Project\" or \"Import from Excel\" to add the first ones."
                                                : "No projects yet.")}
                                    </td></tr>
                                ) : rows.map((r) => (
                                    <tr key={r.id} className="border-b border-[color:var(--asl-border,#E5E7EB)] last:border-b-0"
                                        data-testid={`project-row-${r.id}`}>
                                        {canWrite && (
                                            <td className="px-3 py-2 w-8">
                                                <Checkbox
                                                    checked={bulk.isSelected(r.id)}
                                                    onCheckedChange={() => bulk.toggle(r.id)}
                                                    data-testid={`bulk-select-${r.id}`}
                                                />
                                            </td>
                                        )}
                                        {/* Iter60.p14 — Job # column now leads, styled prominently. */}
                                        <td
                                            className="px-3 py-2 mono font-bold asl-navy-text"
                                            data-testid={`project-code-${r.id}`}
                                        >
                                            {r.code || "—"}
                                        </td>
                                        <td className="px-3 py-2 font-medium">{r.name}</td>
                                        <td className="px-3 py-2">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
                                                {r.active ? "ACTIVE" : "INACTIVE"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 mono text-xs text-[color:var(--asl-muted)]">{(r.created_at || "").slice(0, 10)}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2 justify-end">
                                                {canWrite && (
                                                    <>
                                                        <Button variant="outline" size="sm" onClick={() => setEditRow({ id: r.id, name: r.name, code: r.code || "" })}
                                                            data-testid={`project-edit-${r.id}`}>
                                                            <Edit3 className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button variant="outline" size="sm" onClick={async () => {
                                                            try {
                                                                await api.patch(`/projects/${r.id}`, { active: !r.active });
                                                                toast.success(r.active ? "Deactivated" : "Activated");
                                                                load();
                                                            } catch (e) { toast.error(formatApiError(e)); }
                                                        }} data-testid={`project-toggle-${r.id}`}>
                                                            {r.active ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                                                        </Button>
                                                        <Button variant="outline" size="sm" onClick={() => setConfirmDelete(r)}
                                                            className="text-[#DC2626]" data-testid={`project-delete-${r.id}`}>
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* New project dialog */}
            <NewOrEditDialog
                mode="new"
                open={showNew}
                onOpenChange={setShowNew}
                onSave={async ({ name, code }) => {
                    await api.post("/projects", { module_id: module.id, name, code: code || null });
                    toast.success("Project created");
                    setShowNew(false);
                    load();
                }}
                moduleName={module?.name}
            />

            {/* Edit dialog */}
            <NewOrEditDialog
                mode="edit"
                initial={editRow}
                open={!!editRow}
                onOpenChange={(o) => !o && setEditRow(null)}
                onSave={async ({ name, code }) => {
                    await api.patch(`/projects/${editRow.id}`, { name, code: code || null });
                    toast.success("Project updated");
                    setEditRow(null);
                    load();
                }}
                moduleName={module?.name}
            />

            {/* Delete confirm */}
            <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <DialogContent data-testid="project-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete project?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-[color:var(--asl-muted)]">
                        This will soft-delete <span className="font-semibold asl-navy-text">&ldquo;{confirmDelete?.name}&rdquo;</span>.
                        Attendance rows that reference this project will keep the reference — the project will just no longer
                        be pickable for new entries.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                        <Button className="asl-crimson-bg text-white" onClick={async () => {
                            try {
                                await api.delete(`/projects/${confirmDelete.id}`);
                                toast.success("Project deleted");
                                setConfirmDelete(null);
                                load();
                            } catch (e) { toast.error(formatApiError(e)); }
                        }} data-testid="project-delete-confirm">Delete project</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter60.p14 — Import from Excel dialog */}
            <ImportDialog
                open={showImport}
                onOpenChange={setShowImport}
                moduleId={module?.id}
                moduleName={module?.name}
                onImported={load}
            />
        </>
    );
}

function NewOrEditDialog({ mode, initial, open, onOpenChange, onSave, moduleName }) {
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(initial?.name || "");
            setCode(initial?.code || "");
        }
    }, [open, initial]);

    const submit = async () => {
        if (!name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try { await onSave({ name: name.trim(), code: code.trim() }); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={mode === "new" ? "project-new-dialog" : "project-edit-dialog"}>
                <DialogHeader>
                    <DialogTitle>{mode === "new" ? "New Project" : "Edit Project"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <div className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Module</div>
                        <div className="mt-1 text-sm text-[color:var(--asl-muted)]">{moduleName || "—"} (fixed)</div>
                    </div>
                    <label className="block">
                        <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Name *</span>
                        <input
                            type="text" value={name} onChange={(e) => setName(e.target.value)}
                            className="mt-1 w-full h-9 px-3 border rounded-md text-sm"
                            style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                            data-testid="project-name-input"
                            placeholder="e.g. Alpha Site"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Code (optional)</span>
                        <input
                            type="text" value={code} onChange={(e) => setCode(e.target.value)}
                            className="mt-1 w-full h-9 px-3 border rounded-md text-sm mono"
                            style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                            data-testid="project-code-input"
                            placeholder="e.g. ALPHA"
                            maxLength={20}
                        />
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button className="asl-navy-bg text-white" onClick={submit} disabled={saving} data-testid="project-save-btn">
                        {saving ? "Saving…" : (mode === "new" ? "Create Project" : "Save Changes")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Iter60.p14 — Bulk import projects from an .xlsx / .csv file.
 *
 * Two-phase flow (matches Staff "Import from Document" affordance):
 *   1. User picks a file → we POST to `/api/projects/import` with the
 *      current module_id. Backend upserts.
 *   2. We render the response summary (created / updated / skipped /
 *      failed) with a "Download rejected rows" button when applicable.
 *
 * Never lets the user pick a different module — the current workspace
 * module is baked into the request. The backend also enforces
 * `can_access_module(user, module_id)` so a Dept Mgr can't sneak the
 * scope via crafted payload.
 */
function ImportDialog({ open, onOpenChange, moduleId, moduleName, onImported }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    // Reset state whenever the dialog opens.
    useEffect(() => {
        if (open) { setFile(null); setBusy(false); setResult(null); }
    }, [open]);

    const submit = async () => {
        if (!file || !moduleId) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("module_id", moduleId);
            const { data } = await api.post("/projects/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            const s = data?.summary || {};
            if ((s.created || 0) + (s.updated || 0) > 0) {
                toast.success(`Imported ${s.created} new, ${s.updated} updated`);
                onImported?.();
            }
            if ((s.failed || 0) > 0) {
                toast.warning(`${s.failed} row${s.failed === 1 ? "" : "s"} could not be imported`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const downloadRejects = () => {
        if (!result?.rejected_csv_b64) return;
        const bytes = Uint8Array.from(atob(result.rejected_csv_b64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `projects-import-rejected-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="project-import-dialog">
                <DialogHeader>
                    <DialogTitle>Import Projects from Excel</DialogTitle>
                    <DialogDescription>
                        Bulk-load projects into <span className="font-semibold asl-navy-text">{moduleName}</span>.
                        Existing rows with the same <em>Job #</em> are updated; new rows are created.
                    </DialogDescription>
                </DialogHeader>

                {!result && (
                    <div className="space-y-3">
                        <div className="rounded-md border border-[color:var(--asl-border)] p-3 text-xs bg-slate-50">
                            <div className="font-semibold text-sm mb-1 asl-navy-text">Required columns</div>
                            <ul className="list-disc pl-5 space-y-0.5">
                                <li><span className="mono font-semibold">Job #</span> (or <span className="mono">Job Number</span>) — used as the upsert key.</li>
                                <li><span className="mono font-semibold">Project Name</span> — required.</li>
                                <li><span className="mono">Status</span> — optional. <span className="text-[color:var(--asl-muted)]">&ldquo;Active&rdquo; / &ldquo;Inactive&rdquo; (default: Active)</span></li>
                            </ul>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider asl-navy-text">File</label>
                            <input
                                type="file"
                                accept=".xlsx,.csv"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="mt-1 block w-full text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[color:var(--asl-navy)] file:text-white"
                                data-testid="project-import-file"
                            />
                            <div className="mt-1 text-[10px] text-[color:var(--asl-muted)]">Max 5 MB. Accepts .xlsx or .csv.</div>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="space-y-3" data-testid="project-import-result">
                        <div className="grid grid-cols-4 gap-2">
                            <SummaryPill label="Total" value={result.summary?.total_rows || 0} testid="import-sum-total" />
                            <SummaryPill label="Created" value={result.summary?.created || 0} tone="ok" testid="import-sum-created" />
                            <SummaryPill label="Updated" value={result.summary?.updated || 0} tone="info" testid="import-sum-updated" />
                            <SummaryPill label="Failed" value={result.summary?.failed || 0} tone="err" testid="import-sum-failed" />
                        </div>
                        {result.failed && result.failed.length > 0 && (
                            <div className="border border-[#FCA5A5] bg-[#FEF2F2] rounded p-3 space-y-2" data-testid="import-failed-list">
                                <div className="text-xs font-semibold text-[#991B1B]">
                                    Rows that could not be imported ({result.failed.length}):
                                </div>
                                <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                                    {result.failed.slice(0, 20).map((f, i) => (
                                        <li key={i} className="font-mono">
                                            Row {f.row}: <span className="text-[#991B1B]">{f.reason}</span>
                                        </li>
                                    ))}
                                    {result.failed.length > 20 && (
                                        <li className="italic text-[color:var(--asl-muted)]">… and {result.failed.length - 20} more.</li>
                                    )}
                                </ul>
                                {result.rejected_csv_b64 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={downloadRejects}
                                        data-testid="project-import-download-rejects"
                                    >
                                        <Download className="h-3.5 w-3.5 mr-1" /> Download rejected rows CSV
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        {result ? "Close" : "Cancel"}
                    </Button>
                    {!result && (
                        <Button
                            onClick={submit}
                            disabled={!file || busy}
                            className="asl-navy-bg text-white"
                            data-testid="project-import-submit"
                        >
                            {busy ? "Importing…" : "Import"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SummaryPill({ label, value, tone, testid }) {
    const cls = tone === "ok" ? "bg-[#DCFCE7] text-[#166534]"
        : tone === "err" ? "bg-[#FEE2E2] text-[#991B1B]"
        : tone === "info" ? "bg-[#DBEAFE] text-[#1E3A8A]"
        : "bg-slate-100 text-slate-700";
    return (
        <div className={`rounded-md px-3 py-2 ${cls}`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
            <div className="text-lg font-bold mono">{value}</div>
        </div>
    );
}

